import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import clsx from 'clsx';
import { Button, Grid, Typography } from '@material-ui/core';
import { generateScript } from '@api-v2/artificialIntelligence';
import {
  ScriptType,
  NextToDo,
  VideoFor,
} from '@sendspark/js-api-sdk';

import {
  scritpGeneratorIdentifyTrack,
  scritpGeneratorApiCalledTrack,
  scritpGeneratorScriptGeneratedTrack,
  scritpGeneratorScriptContentOpenedTrack,
  SCRIPT_GENERATOR_METHOD,
} from '@components/SegmentTrackers';
import useTopToastNotification from '@hooks/useTopToastNotification';
import LetsGetStarted from '@components/VideoScriptGenerator/Steps/LetsGetStarted';
import SelectorStep from '@components/VideoScriptGenerator/Steps/SelectorStep';
import {
  productWebsiteStepTextInputData,
  productNameStepTextInputData,
  videoScriptSelectorData,
  viewerNextStepSelectorData,
  workEmailTextInputData,
} from '@components/VideoScriptGenerator/Steps/StepsData';
import TextFieldStep from '@components/VideoScriptGenerator/Steps/TextFieldStep';
import ScriptStep from '@components/VideoScriptGenerator/Steps/ScriptStep';
import useStyles from '@components/VideoScriptGenerator/Layout/styles';
import Modal from '@components/VideoScriptGenerator/Modal';
import { sendsparkSignupUrl } from '@utils/segmentHelpers';
import { EnvConfig } from '@pages/video-script-generator';
import SVGDotsWrap from '@assets/SVGDotsWrap';
import RenderIf from '@components/RenderIf';
import { ScriptData } from '@components/VideoScriptGenerator/types';

export interface StepsProps {
  next: (event?: any) => void;
  back?: (event: any) => void;
  position?: number;
}

const Wizard = ({ env }: { env: EnvConfig }) => {
  const classes = useStyles();
  const [data, setData] = useState<ScriptData>({
    nextToDo: NextToDo.ScheduleAMeeting,
    productCalled: '',
    videoFor: VideoFor.LooksLikeAGoodFit,
    productWebsite: '',
    workEmail: '',
  });
  const [openRateLimitModal, setOpenRateLimitModal] = useState<boolean>(false);
  const [position, setPosition] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [valuesChanged, setValuesChanged] = useState(true);
  const [scriptResult, setScriptResult] = useState<{
    videoScript?: string;
    contentScript?: string;
    subjectScript?: string;
  }>({});
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { notification } = useTopToastNotification();

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const recTokenResult = await executeRecaptcha('scriptGeneratorEmail');
    return recTokenResult;
  }, [executeRecaptcha]);

  const callGenerateScript = ({
    data,
    scriptType,
  }: {
    data: ScriptData;
    scriptType: ScriptType;
  }) => {
    const scriptData = { ...data } as ScriptData;

    if (!scriptData) return;
    setIsLoading(true);

    const contentType = scriptType === 'subject+content' ? 'email' : scriptType;

    scritpGeneratorApiCalledTrack(
      SCRIPT_GENERATOR_METHOD.SCRIPT_GENERATOR_API_CALLED,
      { contentType }
    );
    scritpGeneratorScriptGeneratedTrack(
      SCRIPT_GENERATOR_METHOD.SCRIPT_GENERATOR_SCRIPT_GENERATED,
      data
    );

    generateScript(
      {
        ...scriptData,
        scriptType,
      },
      {
        baseURL: env.apiV2Url,
      }
    )
      .then((response) => {
        setScriptResult({
          ...scriptResult,
          ...response,
        });
        setValuesChanged(false);
      })
      .catch((error) => {
        if (error?.response?.status === 429) {
          setOpenRateLimitModal(true);
          return;
        }

        if (error?.response?.status === 403) {
          notification('AI Server Overloaded, please try again', {
            variant: 'error',
          });

          // add here segment or any other error reporter
          return;
        }

        // here condition includes all 4xx and 5xx errors no handled before
        if (error?.response?.status >= 400) {
          notification('Failed to generate script, please try again', {
            variant: 'error',
          });

          // add here segment or any other error reporter
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const next = (event?: any, submit?: boolean) => {
    const newData: ScriptData = {
      ...data,
      ...event,
    };
    if (event) {
      setData(newData);
    }

    if (submit && valuesChanged) {
      if (newData.workEmail !== undefined && position === 7) {
        scritpGeneratorIdentifyTrack({
          email: newData?.workEmail,
          product: newData?.productCalled,
        });
      }
      callGenerateScript({
        data: {
          ...newData,
          challenge: true,
        },
        scriptType: ScriptType.Video,
      });
    }

    if (position === steps.length - 1) {
      return;
    }

    setPosition((position) => position + 1);
  };

  const back = () => {
    if (position === 0) {
      return;
    }
    setPosition((position) => position - 1);
  };

  const mapTitle = (title: string, text: string, targetText: string) =>
    title.replace(targetText, text);

  const handleGenerateEmailScript = async () => {
    if (valuesChanged || !scriptResult?.contentScript) {
      const captcha = await handleReCaptchaVerify();
      callGenerateScript({
        data: {
          ...data,
          challenge: false,
          'g-recaptcha-response': captcha || '',
        },
        scriptType: ScriptType.Subjectcontent,
      });
    }
  };

  const handleScriptContentOpener = async (text: string, section: string) => {
    scritpGeneratorScriptContentOpenedTrack(
      SCRIPT_GENERATOR_METHOD.SCRIPT_GENERATOR_SCRIPT_CONTENT_OPENED
    );
    window.open(
      `${window.location.href}/view?text=${text}&section=${section}`,
      '_blank',
      'popup=true,width=1000,height=400'
    );
  };

  useEffect(() => {
    setValuesChanged(true);
    setScriptResult({});
  }, [
    data.nextToDo,
    data.productCalled,
    data.videoFor,
    data.productWebsite,
  ]);

  const steps = [
    { component: <LetsGetStarted next={next} /> },
    {
      component: (
        <SelectorStep
          next={next}
          back={back}
          position={position}
          data={videoScriptSelectorData}
          values={data}
        />
      ),
    },
    {
      component: (
        <TextFieldStep
          next={next}
          back={back}
          position={position}
          data={productNameStepTextInputData}
          values={data}
        />
      ),
    },
    {
      component: (
        <TextFieldStep
          next={next}
          back={back}
          position={position}
          data={productWebsiteStepTextInputData}
          values={data}
          mapTitle={() =>
            mapTitle(
              productWebsiteStepTextInputData.title,
              data.productCalled, // TBD: Fix this key as type productNameStepTextInputData?.valueName
              '[product name]'
            )
          }
          displayError
        />
      ),
    },
    {
      component: (
        <SelectorStep
          next={next}
          back={back}
          position={position}
          data={viewerNextStepSelectorData}
          values={data}
        />
      ),
    },
    {
      component: (
        <TextFieldStep
          next={(event: any) => next(event, true)}
          back={back}
          position={position}
          data={workEmailTextInputData}
          env={env}
          values={data}
          useCaptcha
          displayError
        />
      ),
    },
    {
      component: (
        <ScriptStep
          env={env}
          back={back}
          scriptResult={scriptResult}
          generateEmailScript={handleGenerateEmailScript}
          handleScriptContentOpener={handleScriptContentOpener}
          loading={isLoading}
        />
      ),
    },
  ];

  return (
    // the script step (final step) has some different styles
    <>
      <div
        className={clsx({
          [classes.content]: position !== steps.length - 1,
          [classes.lastStepContent]: position === steps.length - 1,
          lastStepContent: position === steps.length - 1,
        })}
      >
        <Grid
          container
          direction="row"
          justify="center"
          alignItems={position === steps.length - 1 ? 'flex-start' : 'center'}
          // the script step (final step) has different height
          style={{
            height: position === steps.length - 1 ? '100%' : 'auto',
            zIndex: 1,
          }}
        >
          {steps[position]?.component}
        </Grid>
        <RenderIf condition={position !== steps.length - 1}>
          <div className={classes.topDots}>
            <SVGDotsWrap />
          </div>
          <div className={classes.bottomDots}>
            <SVGDotsWrap />
          </div>
        </RenderIf>
      </div>
      <Modal
        open={openRateLimitModal}
        title="You've reached your daily limit"
        onClose={() => setOpenRateLimitModal(false)}
        modalCss={{ maxWidth: '680px' }}
        description="Looks like you've hit your daily limit, but don't let that stop you! Think of it as a mini break, a chance to step away and come back refreshed."
      >
        <div className={classes.modalContent}>
          <Typography>
            And while you're at it, why not take a look at SendSpark? It's the
            perfect tool to help you record videos with your generated scripts –
            you'll be glad you did!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component="a"
            href={sendsparkSignupUrl(env)}
            target="_blank"
            rel="noopener"
          >
            Get Started with Sendspark 🎬
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Wizard;
